import { useState, useRef, useEffect, useCallback } from 'react';
import { theme } from '../../theme';

/**
 * SearchableSelect — single-select combobox.
 * The input IS the trigger — no focus-transfer delay.
 *
 * Props:
 *   options     : [{ value, label, meta? }]
 *   value       : selected value (string/number) or ""
 *   onChange    : (value) => void
 *   placeholder : string
 *   disabled    : bool
 *   style       : extra wrapper style
 */
export function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  style = {},
}) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => String(o.value) === String(value));

  // What to show in the input
  const inputDisplay = open ? query : (selected ? selected.label : '');

  const filtered = query.trim()
    ? options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) ||
        (o.meta || '').toLowerCase().includes(query.toLowerCase())
      )
    : options;

  // Close on outside click
  useEffect(() => {
    function onOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const handleFocus = () => {
    if (!disabled) {
      setOpen(true);
      setQuery(''); // clear so user sees all options first
    }
  };

  const handleInput = (e) => {
    setQuery(e.target.value);
    setOpen(true);
  };

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); inputRef.current?.blur(); }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...style }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          value={inputDisplay}
          onChange={handleInput}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '8px 36px 8px 10px',
            border: `1px solid ${open ? theme.primary : theme.border}`,
            borderRadius: 8, fontSize: 14,
            background: disabled ? '#F9FAFB' : '#fff',
            color: theme.text, outline: 'none',
            cursor: disabled ? 'default' : 'text',
          }}
        />
        <span style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center', gap: 4, pointerEvents: 'auto',
        }}>
          {selected && !disabled && (
            <span
              onMouseDown={handleClear}
              style={{ color: '#9CA3AF', fontSize: 13, cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}
            >✕</span>
          )}
          <span style={{ color: '#9CA3AF', fontSize: 10 }}>{open ? '▲' : '▼'}</span>
        </span>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, marginTop: 4,
          background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
        }}>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
                Not available
              </div>
            ) : filtered.map(opt => {
              const isSel = String(opt.value) === String(value);
              return (
                <div
                  key={opt.value}
                  onMouseDown={() => handleSelect(opt)}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                    background: isSel ? `${theme.primary}12` : '#fff',
                    color: isSel ? theme.primary : theme.text,
                    fontWeight: isSel ? 600 : 400,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: '1px solid #F8FAFC',
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#F8FAFC'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isSel ? `${theme.primary}12` : '#fff'; }}
                >
                  <span>{opt.label}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {opt.meta && <span style={{ fontSize: 11, color: '#9CA3AF' }}>{opt.meta}</span>}
                    {isSel && <span style={{ fontSize: 12, color: theme.primary }}>✓</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * SearchableMultiSelect — multi-select combobox with inline search.
 * Selected items appear as tags; typing in the inline input filters options.
 *
 * Props:
 *   options       : [{ value, label }]
 *   values        : [value, ...]
 *   onChange      : (values[]) => void
 *   placeholder   : string
 *   disabled      : bool
 *   style         : extra wrapper style
 *   renderOption  : optional (opt, isSelected) => ReactNode
 *   renderTag     : optional (opt) => ReactNode
 */
export function SearchableMultiSelect({
  options = [],
  values = [],
  onChange,
  placeholder = 'Select…',
  disabled = false,
  style = {},
  renderOption,
  renderTag,
}) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  const selectedOptions = options.filter(o => values.map(String).includes(String(o.value)));

  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    function onOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const toggle = useCallback((opt) => {
    const isSelected = values.map(String).includes(String(opt.value));
    onChange(isSelected
      ? values.filter(v => String(v) !== String(opt.value))
      : [...values, opt.value]
    );
    // keep open for multi-select, just clear query so user can keep picking
    setQuery('');
    inputRef.current?.focus();
  }, [values, onChange]);

  const removeTag = (e, val) => {
    e.stopPropagation();
    onChange(values.filter(v => String(v) !== String(val)));
  };

  const handleWrapClick = () => {
    if (!disabled) {
      if (wrapRef.current) {
        const rect = wrapRef.current.getBoundingClientRect();
        setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
      setOpen(true);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    // Backspace on empty input removes last tag
    if (e.key === 'Backspace' && query === '' && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...style }}>
      {/* Trigger area with tags + inline input */}
      <div
        onClick={handleWrapClick}
        style={{
          minHeight: 38, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4,
          padding: '4px 8px', borderRadius: 8,
          border: `1px solid ${open ? theme.primary : theme.border}`,
          background: disabled ? '#F9FAFB' : '#fff',
          cursor: disabled ? 'default' : 'text',
          boxSizing: 'border-box',
        }}
      >
        {/* Tags */}
        {selectedOptions.map(opt => (
          <span key={opt.value} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: `${theme.primary}15`, color: theme.primary,
            borderRadius: 20, padding: '2px 8px 2px 6px', fontSize: 12, fontWeight: 600,
            flexShrink: 0,
          }}>
            {renderTag ? renderTag(opt) : opt.label}
            {!disabled && (
              <span
                onMouseDown={e => removeTag(e, opt.value)}
                style={{ cursor: 'pointer', fontSize: 11, lineHeight: 1 }}
              >✕</span>
            )}
          </span>
        ))}

        {/* Inline search input */}
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedOptions.length === 0 ? placeholder : ''}
          autoComplete="off"
          style={{
            flex: 1, minWidth: 80, border: 'none', outline: 'none',
            fontSize: 14, background: 'transparent', color: theme.text,
            padding: '2px 4px', cursor: disabled ? 'default' : 'text',
          }}
        />

        <span style={{ color: '#9CA3AF', fontSize: 10, flexShrink: 0, alignSelf: 'center', marginLeft: 'auto' }}>
          {open ? '▲' : '▼'}
        </span>
      </div>

      {/* Dropdown — fixed so it escapes any overflow:hidden/auto ancestor */}
      {open && (
        <div style={{
          position: 'fixed',
          top: dropPos.top, left: dropPos.left, width: dropPos.width,
          zIndex: 9999,
          background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
        }}>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
                Not available
              </div>
            ) : filtered.map(opt => {
              const isSel = values.map(String).includes(String(opt.value));
              return (
                <div
                  key={opt.value}
                  onMouseDown={e => { e.preventDefault(); toggle(opt); }}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                    background: isSel ? `${theme.primary}10` : '#fff',
                    display: 'flex', alignItems: 'center', gap: 8,
                    borderBottom: '1px solid #F8FAFC',
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#F8FAFC'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isSel ? `${theme.primary}10` : '#fff'; }}
                >
                  {/* Checkbox */}
                  <span style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: `1.5px solid ${isSel ? theme.primary : '#D1D5DB'}`,
                    background: isSel ? theme.primary : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isSel && <span style={{ color: '#fff', fontSize: 10, fontWeight: 800 }}>✓</span>}
                  </span>

                  {renderOption ? renderOption(opt, isSel) : (
                    <span style={{ color: isSel ? theme.primary : theme.text, fontWeight: isSel ? 600 : 400 }}>
                      {opt.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {values.length > 0 && (
            <div style={{
              padding: '6px 12px', borderTop: `1px solid ${theme.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 12, color: '#9CA3AF' }}>{values.length} selected</span>
              <button
                type="button"
                onMouseDown={e => { e.preventDefault(); onChange([]); setQuery(''); }}
                style={{ fontSize: 12, color: theme.danger, background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
